import Immutable from 'immutable';

import {
	webapi,
	createModuleState,
	REPORTED_EXCEPTION,
	StaticLookup,
	setPreference,
	getPreference,
	createModelForState
} from 'ps-gui';
import {
	createSearchModel,
	getSqlFulltextFilter,
	listToSet,
	parseRoleId
} from './utils';

const EMPTY_MAP = Immutable.Map({});
const EMPTY_LIST = Immutable.List([]);
const EMPTY_STATE = createModuleState('AffirmationDistributionResult');

export default class AffirmationDistributionResultModel {
	#data;

	constructor(data) {
		this.#data = data || EMPTY_MAP;
	}

	#getPreferenceKeyBase() {
		return '//adrrep//';
	}

	#getExtFilterPreferenceKey() {
		return this.#getPreferenceKeyBase() + 'extFilter';
	}

	#setProperty(name, value) {
		if (Array.isArray(name)) {
			return new AffirmationDistributionResultModel(this.#data.setIn(name, value));
		} else {
			return new AffirmationDistributionResultModel(this.#data.set(name, value));
		}
	}

	async checkGlobalContextChange(globalContext) {
		if (!globalContext) {
			return this;
		}
		const gcLangId = globalContext.getLanguageId();
		const langId = this.#data.get('langId');
		if (gcLangId === langId) {
			return this;
		}
		return (await this.initialize(globalContext)).#setProperty('languageTimestamp', new Date().getTime());
	}

	getLanguageTimestamp() {
		return this.#data.get('languageTimestamp') || 0;
	}

	setSelectedTabId(value) {
		return this.#setProperty('selectedTabId', value);
	}

	setRoleId(roleId) {
		let model = this.#setProperty('roleId', roleId);
		let dp = this.#data.get('distributionPeriodLookup.' + roleId);
		if (dp) {
			model = model.setDistributionPeriodId(dp.getItems().getIn([0, 'key']));
		}
		return model;
	}

	setDistributionPeriodId(value) {
		return this.#setProperty('distributionPeriodId', value);
	}

	setSearchCustomResultProps(value) {
		return this.#setProperty('searchCustomResultProps', value);
	}

	/*setAggregateCustomResultProps(value) {
		return this.#setProperty('aggregateCustomResultProps', value);
	}*/

	async initializeLookups() {
		let pi = await webapi.execute('/api/proc/api_affirmations/getMovieProducerDistributionResultInfo');
		if (pi === REPORTED_EXCEPTION) {
			return this;
		}
		// console.log('pi = ', pi);
		let model = this.#setProperty('roleLookup', StaticLookup(Immutable.fromJS(pi.roles || [])));
		if (pi.distributionPeriods) {
			for (let roleId in pi.distributionPeriods) {
				let items = pi.distributionPeriods[roleId] || [];
				model = model.#setProperty('distributionPeriodLookup.' + roleId, StaticLookup(Immutable.fromJS(items)));
			}
		}

		model = model.#setProperty('stations', processStations(pi.stations));

		const roleId = pi.roles?.[0]?.key;
		return model
			.setRoleId(roleId);
	}

	initializePreferences() {
		let data = this.#data;
		let ef = getPreference(this.#getExtFilterPreferenceKey());
		if (!ef) {
			return this;
		}
		const roleLookup = data.get('roleLookup');
		if (roleLookup) {
			const roleId = ef.get('roleId');
			if (roleId && roleLookup.getText(roleId)) {
				data = data.set('roleId', roleId);
			}
		}
		const distLookup = data.get('distributionPeriodLookup.' + data.get('roleId'));
		if (distLookup) {
			const distPeriodId = ef.get('distributionPeriodId') || distLookup.getItems().getIn([0, 'key']);
			data = data.set('distributionPeriodId', distPeriodId);
		}
		return new AffirmationDistributionResultModel(data);

	}

	async initialize(globalContext) {
		let model = await this.initializeLookups();
		let srch = createSearchModel('/api/dq/sv_movie_producer_distribution_result')
			.setFixedFilter({ party_id: -1 })
			.setProcessFilter(getProcessSearchFilter(model.getData().get('stations')))
			.setEntityName('movieProducerDistributionResult')
			.setOutput(getSearchOutputColumns());
		srch = srch.initializePreferences();
		model = model.setSearch(srch);

		/*
		let agg = createSearchModel('/api/dq/sv_usage_report_aggregate')
			.setFixedFilter({ party_id: -1 })
			.setProcessFilter(processAggregateSearchFilter)
			.setOutput(getAggregateOutputColumns());
		agg = agg.initializePreferences();
		model = model.setAggregate(agg);
*/
		model = model.initializePreferences();
		model = await model.refreshSearch(true);
		return model.#setProperty('langId', globalContext.getLanguageId());
	}

	async refreshSearch(dontSavePreference) {
		const roleId = this.#data.get('roleId');
		if (!roleId) {
			return this;
		}
		const distributionPeriodId = this.#data.get('distributionPeriodId');
		const partyRole = parseRoleId(roleId) || {};
		let fixedFilter = {
			party_id: partyRole.partyId,
			role_type_id: partyRole.roleTypeId,
			distribution_period_id: distributionPeriodId,
		};
		if (!dontSavePreference && roleId && distributionPeriodId) {
			setPreference(this.#getExtFilterPreferenceKey(), Immutable.Map({
				roleId,
				distributionPeriodId
			}));
		}

		let srch = this.getSearch().setFixedFilter(fixedFilter);
		srch = await srch.search(1);
		return this.setSearch(srch);
	}


	setSearch(model) {
		return new AffirmationDistributionResultModel(this.#data.set('search', model.getState()));
	}

	getSearch() {
		return createModelForState(this.#data.get('search'));
	}

	getAggregate() {
		return createModelForState(this.#data.get('aggregate'));
	}

	getData() {
		return this.#data;
	}

	getState() {
		return EMPTY_STATE.set('data', this.#data);
	}
}

const _aggregates = [
	'time_used',
	'spins',
	'ip_gross_amount',
	'np_gross_amount'
].map(columnName => {
	return {
		columnName,
		type: 'sum'
	};
});

const _aggregateAggregates = [
	'spins',
	'ip_gross_amount',
	'np_gross_amount'
].map(columnName => {
	return {
		columnName,
		type: 'sum'
	};
});

export function getAggregateOutputColumns(forExport) {
	if (forExport) {
		return [
			'title',
			'subtitle',
			'isrc',
			'recording_id',
			'recording_type_id',
			'spins',
			'ip_gross_amount',
			'np_gross_amount'
		];
	} else {
		return [
			'recording',
			'recording_type_id',
			'spins',
			'ip_gross_amount',
			'np_gross_amount'
		];
	}
}

export function getSearchOutputColumns(forExport) {
	if (forExport) {
		return [
			'film_id',
			'title',
			//'original_title',
			'production_year',
			'share_pct',
			'gross_amount',
			'cinema_attendance_reward',
			'broadcast_reward',
			'sale_reward',
			// 'cinema_attendance',
			// 'broadcast_effective_runtime',
			// 'total_sale_income',
		];
	} else {
		return [
			'title',
			'share_pct',
			'cinema_attendance_reward',
			'broadcast_reward',
			'sale_reward',
			'gross_amount',
			/*'cinema_attendance',
			'broadcast_effective_runtime',
			'total_sale_income',*/
		];
	}
}

function processAggregateSearchFilter(filter) {
	filter._aggregates = _aggregateAggregates;
	if (filter.recording) {
		filter.recording = getSqlFulltextFilter(filter.recording);
	}
	return filter;
}

function getProcessSearchFilter(stations) {
	stations = stations || EMPTY_LIST;

	return function (filter) {
		if (filter.recording) {
			filter.recording = getSqlFulltextFilter(filter.recording);
		}
		filter._aggregates = _aggregates;

		if (filter.station) {
			const selectedStations = listToSet(filter.station);
			filter.station = null;
			let af = {
				type: 'or',
				nodes: []
			};
			let broadcasterPartyId = [];
			stations.forEach(parent => {
				const parentId = parent.get('parentId');
				if (selectedStations[parentId]) {
					broadcasterPartyId.push(parentId);
				} else {
					const children = parent.get('children');
					if (children && children.size > 0) {
						let childIds = [];
						children.forEach(child => {
							const childId = child.get('childId');
							if (selectedStations[childId]) {
								childIds.push(childId);
							}
						});
						if (childIds.length > 0) {
							af.nodes.push({
								type: 'and',
								nodes: [{
									columnName: 'broadcaster_party_id',
									operator: '=',
									value: parentId
								}, {
									columnName: 'station',
									operator: 'in',
									value: childIds
								}]
							});
						}
					}
				}
			});
			if (broadcasterPartyId.length > 0) {
				af.nodes.push({
					columnName: 'broadcaster_party_id',
					operator: 'in',
					value: broadcasterPartyId
				});
			}
			if (af.nodes.length > 0) {
				filter._advancedFilter = af;
			}
		}
		return filter;
	};
}

function processStations(rows) {
	let res = [];
	let prevParentId = null;
	let currParent = null;
	rows = rows || [];
	rows.forEach(row => {
		const parentId = row.broadcasterPartyId;
		const parentText = row.broadcasterName;
		const childId = row.station;
		if (!parentId) {
			return;
		}
		const childText = row.station;
		if (parentId !== prevParentId || !currParent) {
			prevParentId = parentId;
			currParent = {
				parentId,
				text: parentText,
				children: []
			};
			res.push(currParent);
		}
		if (childId) {
			currParent.children.push({
				parentId,
				childId,
				text: childText
			});
		}
	});
	return Immutable.fromJS(res);
}

